import React, { createContext, useContext, useState, useCallback } from 'react';
import { ToastContainer } from '@/components/ui/Toast';
import type { ToastProps } from '@/components/ui/Toast';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastOptions {
  variant?: ToastVariant;
  title?: string;
  duration?: number; // milliseconds, 0 = no auto-dismiss
}

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => string;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

interface ToastProviderProps {
  children: React.ReactNode;
  position?:
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left'
    | 'top-center'
    | 'bottom-center';
  defaultDuration?: number; // Default auto-dismiss duration (0 = manual dismiss only)
}

export function ToastProvider({
  children,
  position = 'top-right',
  defaultDuration = 0, // Default: manual dismiss
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, options?: ToastOptions): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const newToast: ToastProps = {
        id,
        message,
        variant: options?.variant || 'info',
        title: options?.title,
        duration: options?.duration !== undefined ? options.duration : defaultDuration,
        onClose: hideToast,
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [defaultDuration, hideToast]
  );

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value: ToastContextValue = {
    showToast,
    hideToast,
    clearAllToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} position={position} />
    </ToastContext.Provider>
  );
}
